import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { inr, formatDate, formatStockDisplay } from "@/lib/utils_pharma";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Search, Trash2, Pencil, ChevronDown, ChevronRight, Upload } from "lucide-react";
import MedicineMasterForm from "@/components/MedicineMasterForm";

const BULK_FIELDS = [
  "Brand Name", "Generic Name", "Manufacturer", "HSN Code", "Category", "Product Type", "Packing Unit", "Base Unit",
  "Units Per Pack", "Allowed Selling Units", "GST", "MRP", "Purchase Price", "Low Stock Alert",
];

const FIELD_ALIASES = {
  "brand name": "name",
  "generic name": "generic_name",
  manufacturer: "manufacturer",
  "hsn code": "hsn_code",
  category: "category",
  "product type": "product_type",
  "packing unit": "packing_unit",
  "base unit": "base_unit",
  "units per pack": "units_per_pack",
  "allowed selling units": "allowed_selling_units",
  gst: "gst_percent",
  mrp: "mrp",
  "purchase price": "purchase_price",
  "low stock alert": "low_stock_threshold",
};

const NUMERIC_FIELDS = ["units_per_pack", "gst_percent", "mrp", "purchase_price", "low_stock_threshold"];

const splitCsvLine = (line) => {
  const values = [];
  let current = "";
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const next = line[i + 1];
    if (char === '"' && quoted && next === '"') {
      current += '"';
      i += 1;
    } else if (char === '"') {
      quoted = !quoted;
    } else if (char === "," && !quoted) {
      values.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  values.push(current.trim());
  return values;
};

const parseDelimitedFile = (text) => {
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return [];
  const headers = splitCsvLine(lines[0]).map((h) => FIELD_ALIASES[h.trim().toLowerCase()] || null);
  return lines.slice(1).map((line, index) => {
    const values = splitCsvLine(line);
    const data = {};
    headers.forEach((field, cellIndex) => {
      if (!field) return;
      const value = values[cellIndex]?.trim() ?? "";
      if (field === "allowed_selling_units") {
        data[field] = value ? value.split(/[|;+]/).map((v) => v.trim()).filter(Boolean) : undefined;
      } else if (NUMERIC_FIELDS.includes(field)) {
        data[field] = value === "" ? undefined : Number(value);
      } else {
        data[field] = value;
      }
    });
    return {
      row_number: index + 2,
      data: {
        name: data.name || "",
        generic_name: data.generic_name || "",
        manufacturer: data.manufacturer || "",
        hsn_code: data.hsn_code || "",
        category: data.category || "",
        product_type: data.product_type || "General Product",
        packing_unit: data.packing_unit || "Piece",
        base_unit: data.base_unit || "Piece",
        unit: data.packing_unit || "Piece",
        units_per_pack: Number(data.units_per_pack || 1),
        allowed_selling_units: data.allowed_selling_units || [data.packing_unit || "Piece"],
        gst_percent: Number(data.gst_percent ?? 12),
        mrp: Number(data.mrp || 0),
        purchase_price: Number(data.purchase_price || 0),
        low_stock_threshold: Number(data.low_stock_threshold ?? 10),
      },
    };
  });
};

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [editId, setEditId] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // New batch details tracking states
  const [expanded, setExpanded] = useState({});
  const [batchesCache, setBatchesCache] = useState({});
  const [loadingBatches, setLoadingBatches] = useState({});

  const load = async () => {
    const { data } = await api.get("/medicines", { params: q ? { q } : {} });
    setItems(data);
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const openNew = () => {
    setSelectedMedicine(null);
    setEditId(null);
    setOpen(true);
  };

  const openEdit = (m) => {
    setSelectedMedicine(m);
    setEditId(m.id);
    setOpen(true);
  };

  const remove = async () => {
    if (!deleteTarget) return;
    await api.delete(`/medicines/${deleteTarget.id}`);
    toast.success("Deleted");
    setDeleteTarget(null);
    load();
  };

  const downloadTemplate = () => {
    const sample = [
      BULK_FIELDS.join(","),
      "Paracetamol 500,Paracetamol,ABC Pharma,30049099,Analgesic,Tablet,Strip,Tablet,10,Strip+Tablet,12,25,15,10",
    ].join("\n");
    const blob = new Blob([sample], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "inventory-medicine-upload-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!/\.(csv|xlsx?)$/i.test(file.name)) {
      toast.error("Please upload a CSV or Excel-compatible CSV file");
      return;
    }
    setBulkLoading(true);
    try {
      let data;
      if (/\.xlsx?$/i.test(file.name)) {
        const formData = new FormData();
        formData.append("file", file);
        ({ data } = await api.post("/medicines/bulk/preview-file", formData));
      } else {
        const text = await file.text();
        const rows = parseDelimitedFile(text);
        if (rows.length === 0) {
          toast.error("Upload file must contain headers and at least one medicine row");
          return;
        }
        ({ data } = await api.post("/medicines/bulk/preview", { rows }));
      }
      setBulkRows(data.valid_rows.map((row) => row));
      setBulkPreview(data);
      setBulkOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to preview upload");
    } finally {
      setBulkLoading(false);
    }
  };

  const confirmBulkImport = async () => {
    if (!bulkRows.length) return toast.error("No valid rows to import");
    setBulkLoading(true);
    try {
      const { data } = await api.post("/medicines/bulk/import", { rows: bulkRows });
      toast.success(`Imported ${data.imported_count} medicines`);
      setBulkOpen(false);
      setBulkPreview(null);
      setBulkRows([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to import medicines");
    } finally {
      setBulkLoading(false);
    }
  };

  const toggleExpand = async (id) => {
    const isExpanding = !expanded[id];
    setExpanded((prev) => ({ ...prev, [id]: isExpanding }));

    if (isExpanding && !batchesCache[id]) {
      setLoadingBatches((prev) => ({ ...prev, [id]: true }));
      try {
        const { data } = await api.get(`/medicines/${id}/batches`);
        setBatchesCache((prev) => ({ ...prev, [id]: data.batches || [] }));
      } catch (err) {
        toast.error("Failed to load batch details");
      } finally {
        setLoadingBatches((prev) => ({ ...prev, [id]: false }));
      }
    }
  };

  const getBatchExpiryStatus = (expiryDate) => {
    if (!expiryDate) {
      return { color: "bg-green-100 text-green-800 border-green-200", label: "Healthy" };
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0);

    if (expiry < today) {
      return { color: "bg-red-100 text-red-800 border-red-200", label: "Expired" };
    }
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays <= 90) {
      return { color: "bg-amber-100 text-amber-800 border-amber-200", label: `Expiring Soon (${diffDays}d)` };
    }
    return { color: "bg-green-100 text-green-800 border-green-200", label: "Healthy" };
  };

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Catalogue</div>
          <h1 className="text-4xl font-semibold" style={{ fontFamily: "Chivo" }}>Inventory</h1>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="inventory-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search medicine…"
              className="pl-9 w-64"
            />
          </div>
          <Button onClick={openNew} data-testid="add-medicine-btn">
            <Plus size={16} className="mr-1" /> Add Medicine
          </Button>
          <Button variant="outline" onClick={downloadTemplate}>Template</Button>
          <Button variant="secondary" disabled={bulkLoading} asChild>
            <label className="cursor-pointer">
              <Upload size={16} className="mr-1" /> Bulk Upload
              <input type="file" accept=".csv,.xls,.xlsx" className="hidden" onChange={handleBulkFile} />
            </label>
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]"></TableHead>
              <TableHead>Brand Name</TableHead>
              <TableHead>HSN</TableHead>
              <TableHead className="text-right">MRP</TableHead>
              <TableHead className="text-right">GST%</TableHead>
              <TableHead className="text-right">Total Stock</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                  No medicines yet. Add your first item.
                </TableCell>
              </TableRow>
            )}
            {items.map((m) => (
              <React.Fragment key={m.id}>
                <TableRow
                  className="data-row cursor-pointer select-none"
                  data-testid={`med-row-${m.id}`}
                  onClick={() => toggleExpand(m.id)}
                >
                  <TableCell>
                    {expanded[m.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{m.name}</div>
                    <div className="text-xs text-muted-foreground">{m.generic_name || "Composition not set"}</div>
                    <div className="text-xs text-muted-foreground">{m.manufacturer}</div>
                    {m.packing_unit && (
                      <div className="flex flex-wrap gap-1 mt-1 text-[10px]">
                        <span className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded font-medium border border-slate-200">
                          Pack: {m.packing_unit}
                        </span>
                        <span className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded font-medium border border-slate-200">
                          Base: {m.base_unit} ({m.units_per_pack}/pack)
                        </span>
                        <span className="bg-primary/10 text-primary px-1.5 py-0.5 rounded font-semibold border border-primary/20">
                          Allows: {m.allowed_selling_units?.join(" + ") || m.packing_unit}
                        </span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{m.hsn_code || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{inr(m.mrp)}</TableCell>
                  <TableCell className="text-right tabular-nums">{m.gst_percent}%</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {(() => {
                      const stockInfo = formatStockDisplay(m.stock, m);
                      return (
                        <div className="flex flex-col items-end">
                          {m.stock <= m.low_stock_threshold ? (
                            <Badge variant="destructive" data-testid={`low-badge-${m.id}`} className="font-medium">
                              {stockInfo.primary}
                            </Badge>
                          ) : (
                            <span className="font-medium text-foreground">{stockInfo.primary}</span>
                          )}
                          {stockInfo.secondary && (
                            <span className="text-[11px] text-muted-foreground font-normal mt-0.5">
                              {stockInfo.secondary}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => openEdit(m)} data-testid={`edit-${m.id}`} className="p-1 hover:bg-muted rounded">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => setDeleteTarget(m)} data-testid={`delete-${m.id}`} className="p-1 hover:bg-muted rounded text-destructive ml-1">
                      <Trash2 size={14} />
                    </button>
                  </TableCell>
                </TableRow>

                {expanded[m.id] && (
                  <TableRow>
                    <TableCell colSpan={7} className="bg-muted/40 p-4" onClick={(e) => e.stopPropagation()}>
                      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3 font-semibold">Active Batches</div>
                      {loadingBatches[m.id] ? (
                        <div className="text-xs text-muted-foreground">Loading batches...</div>
                      ) : !batchesCache[m.id] || batchesCache[m.id].length === 0 ? (
                        <div className="text-xs text-muted-foreground">No active batches for this medicine.</div>
                      ) : (
                        <div className="border border-border rounded bg-white overflow-hidden max-w-4xl">
                          <Table>
                            <TableHeader className="bg-muted/50">
                              <TableRow>
                                <TableHead className="text-xs py-2">Batch Number</TableHead>
                                <TableHead className="text-xs py-2">Expiry Date</TableHead>
                                <TableHead className="text-xs py-2 text-right">Current Stock</TableHead>
                                <TableHead className="text-xs py-2 text-right">Purchase Price</TableHead>
                                <TableHead className="text-xs py-2 text-right">MRP</TableHead>
                                <TableHead className="text-xs py-2">Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {batchesCache[m.id].map((batch) => {
                                const status = getBatchExpiryStatus(batch.expiry_date);
                                return (
                                  <TableRow key={batch.id} className="text-xs hover:bg-muted/20">
                                    <TableCell className="font-mono py-1.5">{batch.batch_no || "—"}</TableCell>
                                    <TableCell className="py-1.5">{formatDate(batch.expiry_date)}</TableCell>
                                    <TableCell className="text-right py-1.5 font-medium tabular-nums">
                                      {(() => {
                                        const batchStockInfo = formatStockDisplay(batch.stock, m);
                                        return (
                                          <div>
                                            <div>{batchStockInfo.primary}</div>
                                            {batchStockInfo.secondary && (
                                              <div className="text-[10px] text-muted-foreground font-normal">
                                                {batchStockInfo.secondary}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </TableCell>
                                    <TableCell className="text-right py-1.5">{inr(batch.purchase_price)}</TableCell>
                                    <TableCell className="text-right py-1.5">{inr(batch.mrp)}</TableCell>
                                    <TableCell className="py-1.5">
                                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${status.color}`}>
                                        {status.label}
                                      </span>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                              {batchesCache[m.id].length > 1 && (
                                <TableRow className="text-xs bg-muted/30 border-t-2 border-border">
                                  <TableCell className="py-1.5 font-semibold" colSpan={2}>Total Stock</TableCell>
                                  <TableCell className="text-right py-1.5 font-bold tabular-nums">
                                    {(() => {
                                      const activeTotal = batchesCache[m.id].reduce((sum, b) => sum + (b.stock || 0), 0);
                                      const activeTotalInfo = formatStockDisplay(activeTotal, m);
                                      return (
                                        <div>
                                          <div>{activeTotalInfo.primary}</div>
                                          {activeTotalInfo.secondary && (
                                            <div className="text-[10px] text-muted-foreground font-normal">
                                              {activeTotalInfo.secondary}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </TableCell>
                                  <TableCell colSpan={3}></TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <MedicineMasterForm
          medicine={selectedMedicine}
          medicineId={editId}
          showStock={false}
          showPurchaseSpecificFields={false}
          requireHsn={true}
          onCancel={() => setOpen(false)}
          onSaved={() => {
            toast.success(editId ? "Medicine updated" : "Medicine added");
            if (editId) {
              setBatchesCache((prev) => {
                const c = { ...prev };
                delete c[editId];
                return c;
              });
            }
            setOpen(false);
            load();
          }}
        />
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-5xl" style={{ maxHeight: "90vh", overflowY: "auto" }}>
          <DialogHeader>
            <DialogTitle>Preview Medicine Bulk Upload</DialogTitle>
          </DialogHeader>
          {bulkPreview && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="rounded border p-3"><div className="text-muted-foreground">Valid rows</div><div className="text-2xl font-semibold text-green-700">{bulkPreview.valid_count}</div></div>
                <div className="rounded border p-3"><div className="text-muted-foreground">Invalid rows</div><div className="text-2xl font-semibold text-red-700">{bulkPreview.invalid_count}</div></div>
                <div className="rounded border p-3"><div className="text-muted-foreground">Stock / batch / expiry</div><div className="text-xs font-medium">Not imported</div></div>
              </div>

              {bulkPreview.errors.length > 0 && (
                <div className="rounded border border-red-200 bg-red-50 p-3">
                  <div className="font-semibold text-red-800 mb-2">Row-wise errors</div>
                  <div className="max-h-40 overflow-auto space-y-1 text-sm text-red-700">
                    {bulkPreview.errors.map((err) => <div key={`${err.row_number}-${err.detail}`}>Row {err.row_number}: {err.detail}</div>)}
                  </div>
                </div>
              )}

              <div className="rounded border overflow-auto max-h-80">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Row</TableHead>
                      <TableHead>Brand</TableHead>
                      <TableHead>Generic</TableHead>
                      <TableHead>HSN</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">MRP</TableHead>
                      <TableHead className="text-right">GST%</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bulkPreview.valid_rows.map((row) => (
                      <TableRow key={row.row_number}>
                        <TableCell>{row.row_number}</TableCell>
                        <TableCell>{row.data.name}</TableCell>
                        <TableCell>{row.data.generic_name}</TableCell>
                        <TableCell>{row.data.hsn_code || "—"}</TableCell>
                        <TableCell>{row.data.product_type}</TableCell>
                        <TableCell className="text-right">{inr(row.data.mrp)}</TableCell>
                        <TableCell className="text-right">{row.data.gst_percent}%</TableCell>
                      </TableRow>
                    ))}
                    {bulkPreview.valid_rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">No valid rows to import.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={confirmBulkImport} disabled={bulkLoading || !bulkRows.length}>{bulkLoading ? "Importing…" : "Import Valid Rows"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md" data-testid="delete-medicine-dialog">
          <DialogHeader>
            <DialogTitle>Delete Medicine</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground">
            Delete <span className="font-semibold text-foreground">{deleteTarget?.name}</span>? This action cannot be undone.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={remove}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
